<template>
  <div class="container">
    <div class="card-list">
      <div v-for="(card, index) in cards" :key="index" class="card-container">
        <div class="card-wrapper-container" :class="{ selected: isCardSelected(index) && !hasError(index) }">
          <div class="card-wrapper" :class="{ selected: isCardSelected(index), disabled: hasError(index) || props.modalState.isLoading }" @click="toggleCard(index)">
            <div class="card-row">
              <IconWrapper :name="card.icon" />
              <span class="card-header-title">{{ card.title }}</span>
            </div>
            <div class="select-circle">
              <IconWrapper :name="isCardSelected(index) && !hasError(index) ? 'checkedCircleIcon' : 'uncheckedCircleIcon'" :icon-height="24" />
            </div>
            <span class="card-header-subtitle">{{ card.subtitle }}</span>
          </div>
        </div>
        <span v-if="hasError(index)" class="error-message">{{ getErrorMessage(index) }}</span>
        <span v-if="index === 0 && props.modalState.errors.general" class="error-message">{{ props.modalState.errors.general }}</span>
      </div>
    </div>
    <div v-if="isCardSelected(0) && !hasError(0)" class="input-wrapper">
      <CustomInputValidation
        :class="['email-input', { 'has-error': props.modalState.errors.emails }]"
        :model-value="props.modalState.emails"
        :input-type="CustomInputType.Multiselect"
        :label="t('distribute-package-pub-email-label')"
        :asteriks="true"
        :options="[]"
        :valid-tag="isValidEmail"
        :mode="'tags'"
        :label-key="'value'"
        :value-prop="'value'"
        :searchable="true"
        :clear-on-select="true"
        :clear-on-blur="true"
        :object="true"
        :min-chars="0"
        :create-option="true"
        :tooltip="t('distribute-package-pub-email-tooltip')"
        :max="10"
        :disabled="props.modalState.isLoading"
        @update:model-value="onEmailsChange"
        @change="onChange"
      />
      <span v-if="props.modalState.errors.emails" class="email-error">{{ props.modalState.errors.emails }}</span>
    </div>
    <div v-if="props.modalState.isLoading" class="loading-overlay">
      <LoadingSpinner />
    </div>
  </div>
</template>

<script setup lang="ts">
import CustomInputValidation from '@/components/common/custom-inputs/CustomInputValidation.vue'
import IconWrapper from '@/components/common/icons/IconWrapper.vue'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import { CustomInputType } from '@/interfaces'
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { isValidEmail } from '@/services/utils'

interface Card {
  icon: string
  title: string
  subtitle: string
}

interface ChangePayload {
  id: string | number | null
  valid: boolean
}

const { t } = useI18n()

const emit = defineEmits<{
  updateModalState: [payload: { autoMatch: boolean; selectedOnly: boolean; emails: string[] }]
}>()

const props = withDefaults(
  defineProps<{
    modalState: {
      autoMatch: boolean
      selectedOnly: boolean
      emails: string[]
      isLoading: boolean
      errors: {
        selectedOnly: string
        autoMatch: string
        general: string
        emails: string
      }
    }
    updateValidation?: () => void
  }>(),
  {
    modalState: () => ({
      autoMatch: false,
      selectedOnly: false,
      emails: [],
      isLoading: false,
      errors: { selectedOnly: '', autoMatch: '', general: '', emails: '' }
    }),
    updateValidation: undefined
  }
)

const cards: Card[] = [
  {
    icon: 'penIcon',
    title: t('content-specific'),
    subtitle: t('manually-chosen-publisher')
  },
  {
    icon: 'wandIcon',
    title: t('content-all-publishers'),
    subtitle: t('auto-matched-by-interests')
  }
]

const store = useStore()

const isCardSelected = computed(() => {
  return (index: number) => {
    if (index === 0) return props.modalState.selectedOnly
    if (index === 1) return props.modalState.autoMatch
    return false
  }
})

function hasError(index: number) {
  if (index === 0) return !!props.modalState.errors.selectedOnly
  if (index === 1) return !!props.modalState.errors.autoMatch
  return false
}

function getErrorMessage(index: number) {
  if (index === 0) return props.modalState.errors.selectedOnly
  if (index === 1) return props.modalState.errors.autoMatch
  return ''
}

function updateModalState() {
  if (props.updateValidation) {
    return props.updateValidation()
  }

  const hasAnySelection = props.modalState.autoMatch || props.modalState.selectedOnly
  const hasNoErrors = !props.modalState.errors.autoMatch && !props.modalState.errors.selectedOnly
  const hasValidEmails = !props.modalState.selectedOnly || props.modalState.emails.length > 0

  const isValid = hasAnySelection && hasNoErrors && hasValidEmails
  store.dispatch('modal/setActionValidation', isValid)
}

function toggleCard(index: number) {
  if (hasError(index) || props.modalState.isLoading) {
    return
  }

  const newState = {
    autoMatch: props.modalState.autoMatch,
    selectedOnly: props.modalState.selectedOnly,
    emails: props.modalState.emails
  }

  if (index === 0) {
    newState.selectedOnly = !props.modalState.selectedOnly
  } else if (index === 1) {
    newState.autoMatch = !props.modalState.autoMatch
  }

  emit('updateModalState', newState)
  updateModalState()
}

const validationObject = ref<Record<string | number, boolean>>({})

function onChange(val: ChangePayload) {
  if (val.id !== null) {
    validationObject.value[val.id as string | number] = val.valid
  }
  updateModalState()
}

function onEmailsChange(value: string | number | boolean | object | null) {
  const newEmails = Array.isArray(value) ? (value as string[]) : []

  emit('updateModalState', {
    autoMatch: props.modalState.autoMatch,
    selectedOnly: props.modalState.selectedOnly,
    emails: newEmails
  })
  updateModalState()
}
</script>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

.container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  height: 100%;
  overflow: hidden;

  .card-list {
    display: flex;
    gap: 8px;

    .card-container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      border-radius: 6px;

      .card-wrapper-container {
        padding: 4px;
        border-radius: 6px;

        &.selected {
          background: $rebranding-primary-50;
        }
      }

      .card-wrapper {
        display: flex;
        cursor: pointer;
        padding: 24px;
        flex-direction: column;
        gap: 8px;
        border-radius: 4px;
        border: 1px solid $display-neutrals-300;
        position: relative;
        min-width: 323px;
        transition: border 0.2s ease-in-out;
        background: $white;

        &.selected {
          border: 1px solid $primary-color;

          .card-header-title {
            font-size: 18px;
            font-weight: 700;
          }
        }

        &:hover:not(.disabled) {
          border: 1px solid $primary-color-700;
        }

        &.disabled {
          background-color: $categoriesItemBackground;
          cursor: not-allowed;
          opacity: 0.6;
          border: 1px solid $display-neutrals-300;

          .card-header-title,
          .card-header-subtitle {
            color: $disabledText;
          }

          .select-circle-img {
            opacity: 0.5;
          }
        }

        .card-row {
          display: flex;
          gap: 12px;

          .card-header-title {
            color: $bodyText;
            font-size: 18px;
          }
        }

        .select-circle {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 24px;
          height: 24px;
        }

        .select-circle-img {
          width: 100%;
          height: 100%;
          transition: opacity 0.2s ease-in-out;
        }

        .card-header-subtitle {
          color: $lightGrey;
        }
      }

      .error-message {
        color: $error-red-500;
        font-size: 12px;
        font-weight: 400;
        margin-top: 4px;
      }
    }
  }

  .input-wrapper {
    width: 100%;
    height: 100%;
    margin-top: 16px;
    margin-bottom: 4px;
    max-width: 670px;

    .email-input {
      height: 100%;

      &.has-error {
        margin-bottom: 0px;
      }
    }

    .email-error {
      color: $error-red-500;
      font-size: 12px;
      font-weight: 400;
    }
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
